import React from 'react';
import { HelpCircle, Compass } from 'lucide-react';

/**
 * FollowupChips — renders the two AI-generated suggestion chips below an
 * assistant message. Tapping a chip sends the chip's question as a new user
 * message via the parent's `onSelect` handler.
 *
 * Props:
 *   chips: [{ type: 'clarify' | 'explore', label, question }]
 *   onSelect: (questionString) => void
 *   disabled: bool — set true while another response is streaming
 */
export default function FollowupChips({ chips, onSelect, disabled }) {
  if (!chips || chips.length === 0) return null;

  /**
   * Pick the most-likely-correct text to send when a chip is tapped.
   *
   * We DEFEND against an LLM emitting a chip where `label` and
   * `question` are semantically different — i.e. the visible label
   * says "Want to explore X?" but the hidden question is "Does this
   * make sense?". That mismatch sends the wrong message and confuses
   * the user (they tap one option and get a reply about another).
   *
   * Heuristic: if the question is empty OR diverges meaningfully from
   * the label (label is NOT a prefix or substring of the question, AND
   * the two share few words), fall back to the label — what you see
   * is what you send. Otherwise use the (richer) question.
   */
  const pickTextToSend = (chip) => {
    const label = (chip.label || '').trim();
    const question = (chip.question || '').trim();
    if (!question) return label;
    if (!label) return question;
    const normLabel = label.toLowerCase().replace(/[^\w\s]/g, '');
    const normQ = question.toLowerCase().replace(/[^\w\s]/g, '');
    // If the question already contains the (chip-truncated) label or
    // vice-versa, trust the question — it's the richer phrasing.
    if (normQ.includes(normLabel) || normLabel.includes(normQ)) return question;
    // Otherwise compare token overlap; if very low, the LLM has likely
    // mismatched the two fields, so send the label the user actually saw.
    const labelTokens = new Set(normLabel.split(/\s+/).filter((w) => w.length > 2));
    const qTokens = new Set(normQ.split(/\s+/).filter((w) => w.length > 2));
    let shared = 0;
    labelTokens.forEach((t) => { if (qTokens.has(t)) shared += 1; });
    const minSize = Math.min(labelTokens.size, qTokens.size) || 1;
    const overlapRatio = shared / minSize;
    return overlapRatio >= 0.4 ? question : label;
  };

  return (
    <div
      className="mt-4 flex flex-col gap-2"
      data-testid="followup-chips"
    >
      {chips.map((chip, i) => {
        const Icon = chip.type === 'explore' ? Compass : HelpCircle;
        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(pickTextToSend(chip))}
            className="group text-left flex items-start gap-3 px-4 py-3 rounded-2xl transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              WebkitTapHighlightColor: 'transparent',
            }}
            data-testid={`followup-chip-${chip.type}`}
          >
            <Icon
              className="h-4 w-4 mt-0.5 shrink-0 transition-colors"
              style={{
                color: chip.type === 'explore'
                  ? 'var(--theme-accent)'
                  : 'var(--theme-textSecondary)',
              }}
            />
            <span
              className="text-sm leading-snug"
              style={{ color: 'var(--theme-text)' }}
            >
              {chip.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
